import { NotificationOptions } from '../../types/Notifications.types.js';

import { attachDialog, closeDialog } from '../../util/dialog_helpers.js';
import { addPageAction } from '../../util/new_relic.js';
import { getNotificationService } from '../../util/notification_service.js';
import { getData } from '../../util/script_data.js';
import { getUserPrefs, updatePrefs } from '../../util/user_prefs_util.js';

import { showCCDLaunchFailureDialog } from './CCDLaunchFailureDialog.js';

interface NeueI18nInfo {
  [key: string]: {
    [key: string]: string;
  }
}

export interface FontActivationDialogOptions {
  label?: string;
  message?: string;
  queryParams?: Record<string, string>;
}

const notificationService = getNotificationService();
const TOAST_TIMEOUT_MS = 16000;

export class FontActivationFirstMileDialog {
  private successNotificationCallback = () => {};

  async shouldShowFontActivationFirstMileDialog(): Promise<boolean> {
    const userPrefs = await getUserPrefs();
    return !userPrefs.hideUseModelFontActivationModal;
  }

  async showFontActivationFirstMileDialog(queryParams = {}): Promise<void> {
    try {
      const { dialog, dialogPromise } = await attachDialog('font_activation_use_model_dialog') as { dialog: HTMLElement, dialogPromise: Promise<void> };

      this.attachEventHandlers(dialog, queryParams);

      return dialogPromise;
    } catch (error) {
      console.error('Error fetching dialog template:', error);
      throw error;
    }
  }

  handleFirstMileFontActivation(opts: FontActivationDialogOptions = {}) {
    this.successNotificationCallback = handleFirstMileFontActivationSuccess(opts);

    return this.shouldShowFontActivationFirstMileDialog().then(shouldShowDialog => {
      if (shouldShowDialog) {
        return this.showFontActivationFirstMileDialog(opts.queryParams);
      } else {
        this.successNotificationCallback();
        return Promise.resolve();
      }
    });
  }

  private handleButtonClick(optOutCheckbox?: boolean): void {
    if (optOutCheckbox) {
      addPageAction('typekit.click.use-model-font-activation-opt-out');
      this.updatePrefs(optOutCheckbox);
    }

    closeDialog();
    this.successNotificationCallback();
  }

  private attachEventHandlers(dialog: HTMLElement, queryParams = {}): void {
    const checkbox = dialog.querySelector('[data-id="font-activation-use-model-checkbox"]') as HTMLInputElement;
    const openCCDButton = dialog.querySelector('[data-id="adobe-fonts-first-mile-dialog__open-ccd-button"]');
    if (openCCDButton) {
      openCCDButton.addEventListener('click', () => {
        addPageAction('typekit.click.use-model-font-activation-open-app', { source: 'Modal Dialog' });
        this.handleButtonClick(checkbox?.checked);
        this.openCCD(queryParams);
      }, { once: true });
    }

    const okButton = dialog.querySelector('[data-id="adobe-fonts-first-mile-dialog__ok-button"]');
    if (okButton) {
      okButton.addEventListener('click', () => {
        addPageAction('typekit.click.use-model-font-activation-close');
        this.handleButtonClick(checkbox?.checked);
      }, { once: true });
    }
  }

  private openCCD(queryParams = {}): void {
    openCreativeCloudDesktop(queryParams);
  }

  private updatePrefs(optOutCheckbox?: boolean): void {
    updatePrefs({ hideUseModelFontActivationModal: optOutCheckbox || false });
  }
}

export function handleFirstMileFontActivationSuccess(opts: FontActivationDialogOptions = {}) {
  const queryParams = opts.queryParams;
  const i18n = getData('/neue/i18n') as NeueI18nInfo;
  const namespace = 'neue.browse.family_card';
  const actionLabel = i18n[namespace]['open_app'];
  const notificationMessage = i18n[namespace]['fonts_added_success_message'];

  return () => {
    const notificationOptions = {
      actionLabel,
      callbackAction: () => handleOpenCCDButton(queryParams),
      queryParams,
      timeout: TOAST_TIMEOUT_MS
    } as NotificationOptions;

    notificationService.success(notificationMessage, notificationOptions);
  };
}

function handleOpenCCDButton(queryParams = {}): void {
  addPageAction('typekit.click.use-model-font-activation-open-app', { source: 'Success Toast' });
  openCreativeCloudDesktop(queryParams);
}

export async function openCreativeCloudDesktop(queryParams = {}): Promise<void> {
  try {
    const { openCCD } = await import('../../util/WAMClient.js');
    await openCCD(function(isCCDConnectionSuccessful: boolean, isCCDPresent: boolean) {
      if (!isCCDPresent || !isCCDConnectionSuccessful) {
        showCCDLaunchFailureDialog();
      }
    }, queryParams);
  } catch (error) {
    console.warn('WAMClient error:', error);
    showCCDLaunchFailureDialog();
  }
}

// Factory function for backward compatibility
export function handleFirstMileFontActivation(opts: FontActivationDialogOptions = {}) {
  const dialog = new FontActivationFirstMileDialog();
  return dialog.handleFirstMileFontActivation(opts);
}
